/**
 * EventLayout — V2 reskin
 * Sticky ev-head (breadcrumb + event name + status badge) + tabstrip.
 * Uses CSS variables throughout → day/night safe.
 * All API calls / data flow unchanged.
 */

import { useState, useEffect } from 'react';
import { NavLink, useParams, useNavigate, useLocation } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

const STATUS_CFG = {
  draft:     { label: 'Draft',     vars: { bg: 'rgba(245,200,40,0.12)', color: '#92400E', dot: '#F59E0B' } },
  published: { label: 'Published', vars: { bg: 'rgba(16,185,129,0.12)', color: '#065F46', dot: '#10B981' } },
  live:      { label: 'Live',      vars: { bg: 'rgba(239,68,68,0.12)',  color: '#991B1B', dot: '#EF4444' } },
  completed: { label: 'Completed', vars: { bg: 'var(--surface-2)',      color: 'var(--ink-3)', dot: 'var(--line-2)' } },
  cancelled: { label: 'Cancelled', vars: { bg: 'var(--surface-2)',      color: 'var(--ink-3)', dot: 'var(--line-2)' } },
  expired:   { label: 'Expired',   vars: { bg: 'var(--surface-2)',      color: 'var(--ink-3)', dot: 'var(--line-2)' } },
};

const TABS = [
  { to: 'edit',       label: 'Setup'            },
  { to: 'dashboard',  label: 'Dashboard'        },
  { to: 'attendees',  label: 'Attendees'        },
  { to: 'configure',  label: 'Zones & Tickets'  },
  { to: 'team',       label: 'Team'             },
  { to: 'analytics',  label: 'Analytics'        },
  { to: 'live',       label: 'Live'             },
];

export default function EventLayout({ children }) {
  const { id }    = useParams();
  const navigate  = useNavigate();
  const location  = useLocation();
  const [event, setEvent] = useState(null);

  useEffect(() => {
    if (!id) return;
    fetch(`/api/events/${id}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.event) setEvent(data.event); })
      .catch(() => {});
  }, [id]);

  const status    = event?.computed_status ?? event?.status ?? 'draft';
  const cfg       = STATUS_CFG[status] || STATUS_CFG.draft;
  const eventName = event?.name || '…';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>

      {/* ── Event sub-header ────────────────────────────────────────── */}
      <div className="ev-head" style={{
        position: 'sticky', top: 68, zIndex: 40,
        background: 'var(--surface)', borderBottom: '1px solid var(--line)',
        boxShadow: '0 1px 0 var(--line)',
        padding: '12px 28px 0', marginBottom: 0,
      }}>

        {/* Crumb row */}
        <div className="crumb">
          <button
            onClick={() => navigate('/events')}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--ink-3)', fontSize: 13, fontWeight: 500,
              padding: 0, fontFamily: 'inherit',
              transition: 'color .15s',
            }}
            onMouseEnter={e => e.currentTarget.style.color = 'var(--primary)'}
            onMouseLeave={e => e.currentTarget.style.color = 'var(--ink-3)'}
          >
            Events
          </button>

          <ChevronRight size={14} color="var(--ink-3)" style={{ flexShrink: 0 }} />

          <span style={{
            fontSize: 14, fontWeight: 700, color: 'var(--ink)',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            flex: 1, minWidth: 0,
          }}>
            {eventName}
          </span>

          {/* Status badge */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 5,
            background: cfg.vars.bg, borderRadius: 20,
            padding: '4px 10px', flexShrink: 0,
          }}>
            <div style={{
              width: 6, height: 6, borderRadius: '50%', background: cfg.vars.dot, flexShrink: 0,
              ...(status === 'live' ? { animation: 'pulse 1.5s infinite' } : {}),
            }} />
            <span style={{
              fontSize: 11, fontWeight: 700, color: cfg.vars.color,
              textTransform: 'uppercase', letterSpacing: '0.05em',
            }}>
              {cfg.label}
            </span>
          </div>
        </div>

        {/* Tabstrip */}
        <div className="tabstrip">
          {TABS.map(tab => {
            const fullPath = `/events/${id}/${tab.to}`;
            const isActive = location.pathname === fullPath ||
              (tab.to === 'edit' && location.pathname.startsWith(`/events/${id}/edit`));
            return (
              <NavLink
                key={tab.to}
                to={fullPath}
                className={isActive ? 'active' : undefined}
                style={{
                  display: 'inline-flex', alignItems: 'center',
                  padding: '10px 16px',
                  textDecoration: 'none',
                  fontSize: 13,
                  fontWeight: isActive ? 700 : 500,
                  color: isActive ? 'var(--primary)' : 'var(--ink-3)',
                  borderBottom: `2px solid ${isActive ? 'var(--primary)' : 'transparent'}`,
                  whiteSpace: 'nowrap',
                  transition: 'color .15s, border-color .15s',
                  position: 'relative', top: 1,
                }}
                onMouseEnter={e => { if (!isActive) e.currentTarget.style.color = 'var(--ink-2)'; }}
                onMouseLeave={e => { if (!isActive) e.currentTarget.style.color = 'var(--ink-3)'; }}
              >
                {tab.label}
              </NavLink>
            );
          })}
        </div>
      </div>

      {/* ── Page content ─────────────────────────────────────────────── */}
      <div style={{ flex: 1 }}>
        {children}
      </div>
    </div>
  );
}
